import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { resolveDeliveryCarrierLogoUrl } from '../utils/resolveDeliveryCarrierLogoUrl';

export type DeliveryCarrierKey = 'colissimo' | 'chronopost' | 'mondial_relay' | 'ups';

type Props = {
  carrier: DeliveryCarrierKey;
  label: string;
  currentLogoUrl?: string | null;
  onLogoChange: (logoUrl: string | null) => void;
  disabled?: boolean;
};

const ALLOWED_LOGO_EXTENSIONS = ['svg', 'png', 'jpg', 'jpeg'] as const;

function isAllowedLogoFile(file: File): boolean {
  const ext = file.name.toLowerCase().split('.').pop() || '';
  const normalizedExt = ext === 'jpeg' ? 'jpg' : ext;
  if (ALLOWED_LOGO_EXTENSIONS.includes(normalizedExt as (typeof ALLOWED_LOGO_EXTENSIONS)[number])) {
    return true;
  }
  return (
    file.type === 'image/svg+xml' ||
    file.type === 'image/svg' ||
    file.type === 'image/png' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/jpg'
  );
}

export default function DeliveryCarrierLogoUpload({
  carrier,
  label,
  currentLogoUrl,
  onLogoChange,
  disabled = false,
}: Props) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrl = resolveDeliveryCarrierLogoUrl(currentLogoUrl);

  const resolveHttpErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
    const status = e?.response?.status;
    const backendMessage = e?.response?.data?.error || e?.response?.data?.message;
    if (backendMessage) {
      return status ? `[${status}] ${backendMessage}` : backendMessage;
    }
    if (status === 413) {
      return 'Le serveur refuse le fichier (413). Vérifiez la limite upload du serveur.';
    }
    return e?.message || fallback;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedLogoFile(file)) {
      setError('Formats autorisés : SVG, PNG ou JPG.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 4 Mo.');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await apiService.uploadDeliveryCarrierLogo(carrier, formData);
      onLogoChange(response.logo_url);
    } catch (err) {
      setError(resolveHttpErrorMessage(err, 'Erreur lors de l\'upload du logo'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!currentLogoUrl) return;
    setUploading(true);
    setError('');
    try {
      await apiService.deleteDeliveryCarrierLogo(carrier);
      onLogoChange(null);
    } catch (err) {
      setError(resolveHttpErrorMessage(err, 'Erreur lors de la suppression du logo'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Logo {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        SVG ou PNG recommandé — affiché sur la page d&apos;accueil et dans les écrans de livraison.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: 'grey.300',
          position: 'relative',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={disabled ? undefined : () => fileInputRef.current?.click()}
      >
        {previewUrl ? (
          <Box>
            <Box
              component="img"
              src={previewUrl}
              alt={`Logo ${label}`}
              sx={{
                maxHeight: 56,
                maxWidth: 180,
                objectFit: 'contain',
                mx: 'auto',
                mb: 1.5,
                display: 'block',
              }}
            />
            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={uploading || disabled}
              >
                Remplacer
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDeleteLogo();
                }}
                disabled={uploading || disabled}
              >
                Supprimer
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <ImageIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Cliquez pour ajouter un logo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SVG, PNG ou JPG — max 4 Mo
            </Typography>
          </Box>
        )}

        {uploading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.8)',
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}
      </Paper>

      <input
        ref={fileInputRef}
        type="file"
        accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
        onChange={(e) => void handleFileSelect(e)}
        style={{ display: 'none' }}
        disabled={disabled}
      />
    </Box>
  );
}
